import {
  Icon,
  LoadingIndicator,
  MUIButton,
  MUIChip,
  MUIDialog,
  MUITextField,
} from "@abb/arcadia-ui-react"

import { StatusCodes } from "http-status-codes"
import { useCallback, useState } from "react"
import { toast } from "react-toastify"
import { digitalManagerApi } from "../../../../../services/api"
import {
  ApplicationRoleApproverGet,
  ApplicationRoleApproverTypeEnum,
} from "../../../../models/ApplicationRoleApprover"
import { Guid } from "../../../../models/CustomType"
import { RoleRequestGet } from "../../../../models/RoleRequest"
import {
  RoleRequestApproval,
  RoleRequestApprovalActionEnum,
  RoleRequestApprovalStatusEnum,
} from "../../../../models/RoleRequestApproval"
import { getRoleRequestApprovalStatusProps } from "../../../../utils/getRoleRequestApprovalStatusProps"
import { DiMaApprovalTooltip } from "../../../partials/DiMaApprovalTooltip"

const grid12 = "repeat(12, 1fr)"

interface ConfirmDialogProps {
  open: boolean
  roleRequestId?: Guid
  rraId?: Guid
  roleRequestAction?: RoleRequestApprovalActionEnum
  propertyName?: string
  propertyValue?: string
}

interface CommentDialogProps {
  open: boolean
  rraStatus: RoleRequestApprovalStatusEnum
  rraComment: string
}

interface ForwardMessage {
  message: string
  label: string
}

const ulStyle = {
  margin: "2px",
  paddingRight: "15px",
  maxHeight: "100px",
  overflow: "auto",
}

const FORWARD_MESSAGE_DEFAULT_LABEL = "Insert message here"
const FORWARD_MESSAGE_MAX_CHARS = 150
const SHADE = 500
const ICON_NAME_FEEDBACK = "icon_abb_dias icon-abb_feedback"

export const fromActionVerbToActionName = (action: RoleRequestApprovalActionEnum) => {
  switch (action) {
    case RoleRequestApprovalActionEnum.Approve:
      return "approval"
    case RoleRequestApprovalActionEnum.Reject:
      return "rejection"
    case RoleRequestApprovalActionEnum.Forward:
      return "forwarding"
    default:
      return ""
  }
}

export const fromStatusVerbToApproverName = (status: RoleRequestApprovalStatusEnum | null) => {
  switch (status) {
    case RoleRequestApprovalStatusEnum.Approved:
      return "approver"
    case RoleRequestApprovalStatusEnum.Rejected:
      return "rejecter"
    case RoleRequestApprovalStatusEnum.Forwarded:
      return "forwarder"
    default:
      return ""
  }
}

export function RoleRequestApprovals(props: {
  readonly roleRequest: RoleRequestGet
  readonly reloadRoleRequest: () => void
  readonly isLoadingApprovals: boolean
}) {
  const { roleRequest, reloadRoleRequest, isLoadingApprovals } = props
  const applicationId = props.roleRequest.application.id
  const roleId = props.roleRequest.role.id
  const roleRequestId = props.roleRequest.id

  const [isApproving, setIsApproving] = useState<boolean>(false)
  const [forwardBackedupArray, setForwardBackedupArray] = useState<Array<Guid>>([])

  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogProps>({
    open: false,
  })

  const [commentDialog, setCommentDialog] = useState<CommentDialogProps>({
    open: false,
    rraStatus: RoleRequestApprovalStatusEnum.Open,
    rraComment: "",
  })

  const [isLoadingForwarded, setIsLoadingForwarded] = useState<boolean>(false)
  const [forwardListError, setForwardListError] = useState<boolean>(false)
  const [forwardArray, setForwardArray] = useState<string[] | null>([])

  const [forwardMessage, setForwardMessage] = useState<ForwardMessage>({
    message: "",
    label: FORWARD_MESSAGE_DEFAULT_LABEL,
  })

  const renderRraStatusChip = useCallback(
    (rra: RoleRequestApproval) =>
      rra.status === RoleRequestApprovalStatusEnum.Approved ||
      rra.status === RoleRequestApprovalStatusEnum.Rejected ||
      (rra.status === RoleRequestApprovalStatusEnum.Forwarded &&
        rra.userApproverType !== ApplicationRoleApproverTypeEnum.Consulted) ||
      rra.userApproverType === null ||
      rra.userApproverType === ApplicationRoleApproverTypeEnum.Informed ||
      (rra.userApproverType === ApplicationRoleApproverTypeEnum.Consulted &&
        rra.status !== RoleRequestApprovalStatusEnum.Forwarded),
    []
  )

  const retrieveForwarded = useCallback(
    (propertyName: string, propertyValue: string) => {
      setIsLoadingForwarded(true)

      const url =
        propertyName && propertyValue
          ? `/applications/${applicationId}/roles/${roleId}/approvers?approverType=${ApplicationRoleApproverTypeEnum.Consulted}&propertyName=${propertyName}&propertyValue=${propertyValue}`
          : `/applications/${applicationId}/roles/${roleId}/approvers?approverType=${ApplicationRoleApproverTypeEnum.Consulted}`

      digitalManagerApi
        .get<ApplicationRoleApproverGet[]>(url)
        .then((res) => {
          if (res.status === StatusCodes.OK) {
            const tempForwardList: string[] = []
            for (const approver of res.data) {
              tempForwardList.push(approver.user.username)
            }
            setForwardArray(tempForwardList)
            if (tempForwardList.length === 0) {
              setForwardListError(true)
            } else {
              setForwardListError(false)
            }
          }
        })
        .catch(() => {
          setForwardArray(null)
          setForwardListError(true)
        })
        .finally(() => {
          setIsLoadingForwarded(false)
        })
    },
    [applicationId, roleId]
  )

  const openDialogOnClick = useCallback(
    (rra: RoleRequestApproval, rraAction: RoleRequestApprovalActionEnum) => {
      setConfirmDialog({
        roleRequestId,
        open: true,
        rraId: rra.id,
        roleRequestAction: rraAction,
        propertyName: RoleRequestApprovalActionEnum.Forward
          ? rra.property.name
          : undefined,
        propertyValue: RoleRequestApprovalActionEnum.Forward
          ? rra.property.label
          : undefined,
      })
    },
    [roleRequestId]
  )

  const resetForwardMessage = useCallback(() => {
    setForwardMessage({
      message: "",
      label: FORWARD_MESSAGE_DEFAULT_LABEL,
    })
  }, [])

  const setConfirmDialogOpenFalse = useCallback(() => {
    setConfirmDialog({ ...confirmDialog, open: false })
  }, [confirmDialog])

  const setCommentDialogOpenFalse = useCallback(() => {
    setCommentDialog({ ...commentDialog, open: false })
  }, [commentDialog])

  const formatMessageReset = useCallback(() => {
    if (forwardMessage.message === "") {
      resetForwardMessage()
    }
  }, [forwardMessage.message, resetForwardMessage])

  const setCommentDialogOpenTrueCompletion = useCallback(
    (rra: RoleRequestApproval) => () =>
      setCommentDialog({
        open: true,
        rraStatus: rra.status,
        rraComment: rra.completionComment ?? rra.forwardingComment,
      }),
    [setCommentDialog]
  )
  const setForwardBackedupArrayId = useCallback(
    (rra: RoleRequestApproval) => () => {
      setForwardBackedupArray([...forwardBackedupArray, rra.id])
    },
    [forwardBackedupArray]
  )

  const openDialogOnClickApprove = useCallback(
    (rra: RoleRequestApproval) => () => {
      openDialogOnClick(rra, RoleRequestApprovalActionEnum.Approve)
    },
    [openDialogOnClick]
  )

  const openDialogOnClickReject = useCallback(
    (rra: RoleRequestApproval) => () => {
      openDialogOnClick(rra, RoleRequestApprovalActionEnum.Reject)
    },
    [openDialogOnClick]
  )

  const setCommentDialogOpenTrueForwarding = useCallback(
    (rra: RoleRequestApproval) => () =>
      setCommentDialog({
        open: true,
        rraStatus: rra.status,
        rraComment: rra.forwardingComment ?? "",
      }),
    []
  )

  const setForwardBackedupArrayUpdate = useCallback(
    (rra: RoleRequestApproval) => () => {
      const updatedArr = [...forwardBackedupArray]
      updatedArr.splice(forwardBackedupArray.indexOf(rra.id), 1)
      setForwardBackedupArray(updatedArr)
    },
    [forwardBackedupArray]
  )

  const resetForwardMessageApprove = useCallback(
    (rra: RoleRequestApproval) => () => {
      resetForwardMessage()
      openDialogOnClick(rra, RoleRequestApprovalActionEnum.Approve)
    },
    [openDialogOnClick, resetForwardMessage]
  )

  const resetForwardMessageReject = useCallback(
    (rra: RoleRequestApproval) => () => {
      resetForwardMessage()
      openDialogOnClick(rra, RoleRequestApprovalActionEnum.Reject)
    },
    [openDialogOnClick, resetForwardMessage]
  )

  const retrieveForwardedForward = useCallback(
    (rra: RoleRequestApproval) => () => {
      retrieveForwarded(rra.property.name, rra.property.value)
      resetForwardMessage()
      openDialogOnClick(rra, RoleRequestApprovalActionEnum.Forward)
    },
    [openDialogOnClick, resetForwardMessage, retrieveForwarded]
  )

  const handleForwardMessageChange = useCallback((value: string) => {
    setForwardMessage({
      message: value,
      label: `Remaining characters: ${
        FORWARD_MESSAGE_MAX_CHARS - value.length
      }/${FORWARD_MESSAGE_MAX_CHARS}`,
    })
  }, [])

  const onClick = useCallback(() => {
    setIsApproving(true)

    digitalManagerApi
      .put(
        `/roleRequests/${confirmDialog.roleRequestId}/approvals/${confirmDialog.rraId}`,
        { comment: forwardMessage.message },
        {
          params: {
            action: confirmDialog.roleRequestAction,
          },
        }
      )
      .then(() => {
        toast.success("Action completed")
        reloadRoleRequest()
      })
      .catch(() => {
        toast.error("Cannot complete the action")
      })
      .finally(() => {
        setConfirmDialog({ ...confirmDialog, open: false })
        setIsApproving(false)
      })
  }, [confirmDialog, forwardMessage.message, reloadRoleRequest])

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 24,
        padding: 24,
        width: "100%",
      }}
    >
      <h2 className="abb-label">Approvals</h2>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {roleRequest.approvals.map((rra) => (
          <div key={rra.id} className="approval-box">
            <div className="abb-label">
              {rra.property.name ? `${rra.property.name}:` : "Generic"}
            </div>
            <div className="approval-subtitle">{rra.property.label ?? ""}</div>

            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {renderRraStatusChip(rra) ? (
                <>
                  {forwardBackedupArray.includes(rra.id) ? (
                    <>
                      <div style={{ display: "flex", flexDirection: "row", gap: "8px" }}>
                        <MUIButton
                          label="Approve"
                          iconName="icon_abb_dias icon-abb_completed-check"
                          variant="success"
                          size={40}
                          isLoading={false}
                          disabled={isApproving || isLoadingApprovals}
                          onClickButton={openDialogOnClickApprove(rra)}
                        />
                        <MUIButton
                          label="Reject"
                          iconName="icon_abb_dias icon-abb_cancel-circle"
                          variant="error"
                          size={40}
                          isLoading={false}
                          disabled={isApproving || isLoadingApprovals}
                          onClickButton={openDialogOnClickReject(rra)}
                        />
                      </div>
                      {rra.forwardingComment && (
                        <Icon
                          iconName={ICON_NAME_FEEDBACK}
                          size={24}
                          onClick={setCommentDialogOpenTrueForwarding(rra)}
                        />
                      )}
                      <Icon
                        iconName="icon_abb_dias icon-abb_restore"
                        size={24}
                        onClick={setForwardBackedupArrayUpdate(rra)}
                      />
                    </>
                  ) : (
                    <>
                      <DiMaApprovalTooltip
                        roleRequestApproval={rra}
                        applicationId={roleRequest.application.id}
                        roleId={roleRequest.role.id}
                      >
                        <MUIChip {...getRoleRequestApprovalStatusProps(rra.status)} />
                      </DiMaApprovalTooltip>
                      {(rra.completionComment || rra.forwardingComment) && (
                        <Icon
                          iconName={ICON_NAME_FEEDBACK}
                          size={24}
                          onClick={setCommentDialogOpenTrueCompletion(rra)}
                        />
                      )}
                      {rra.status === RoleRequestApprovalStatusEnum.Forwarded &&
                        (rra.userApproverType ===
                          ApplicationRoleApproverTypeEnum.Approver ||
                          rra.userApproverType ===
                            ApplicationRoleApproverTypeEnum.BackupApprover) && (
                          <Icon
                            iconName="icon_abb_dias icon-abb_restore"
                            size={24}
                            onClick={setForwardBackedupArrayId(rra)}
                          />
                        )}
                    </>
                  )}
                </>
              ) : (
                <div style={{ display: "flex", flexDirection: "row", gap: "8px" }}>
                  <MUIButton
                    label="Approve"
                    iconName="icon_abb_dias icon-abb_completed-check"
                    variant="success"
                    size={40}
                    isLoading={false}
                    disabled={isApproving || isLoadingApprovals}
                    onClickButton={resetForwardMessageApprove(rra)}
                  />
                  <MUIButton
                    label="Reject"
                    iconName="icon_abb_dias icon-abb_cancel-circle"
                    variant="error"
                    size={40}
                    isLoading={false}
                    disabled={isApproving || isLoadingApprovals}
                    onClickButton={resetForwardMessageReject(rra)}
                  />
                  {!(
                    rra.userApproverType === ApplicationRoleApproverTypeEnum.Consulted &&
                    rra.status === RoleRequestApprovalStatusEnum.Forwarded
                  ) && (
                    <MUIButton
                      label="Forward"
                      iconName="icon_abb_dias icon-abb_forward"
                      variant="primary"
                      size={40}
                      isLoading={false}
                      disabled={isApproving || isLoadingApprovals}
                      onClickButton={retrieveForwardedForward(rra)}
                    />
                  )}
                </div>
              )}

              {rra.userApproverType === ApplicationRoleApproverTypeEnum.Consulted &&
                rra.status === RoleRequestApprovalStatusEnum.Forwarded && (
                  <>
                    {rra.forwardingComment && (
                      <Icon
                        iconName={ICON_NAME_FEEDBACK}
                        size={24}
                        onClick={setCommentDialogOpenTrueForwarding(rra)}
                      />
                    )}
                  </>
                )}
            </div>
          </div>
        ))}
      </div>

      <MUIDialog
        id="confirm-dialog"
        variant="information"
        open={confirmDialog.open}
        onClose={setConfirmDialogOpenFalse}
        heading={
          !(
            forwardListError &&
            confirmDialog.roleRequestAction === RoleRequestApprovalActionEnum.Forward
          ) && !isLoadingForwarded
            ? `Are you sure to ${confirmDialog.roleRequestAction
                ?.toString()
                .toLocaleLowerCase()} the role request?`
            : undefined
        }
        content={
          <>
            {!(
              forwardListError &&
              confirmDialog.roleRequestAction === RoleRequestApprovalActionEnum.Forward
            ) &&
              !isLoadingForwarded && (
                <>
                  This operation cannot be undone.
                  <br />
                  <br />
                </>
              )}
            {confirmDialog.roleRequestAction ===
              RoleRequestApprovalActionEnum.Forward && (
              <>
                {isLoadingForwarded && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: grid12,
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ gridColumn: "span 12", textAlign: "center" }}>
                      <LoadingIndicator variant="lazy-load" size="medium" />
                    </div>
                  </div>
                )}
                {!isLoadingForwarded && !forwardArray && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: grid12,
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ gridColumn: "span 12", textAlign: "center" }}>
                      <Icon iconName="icon_abb_dias icon-abb_warning" size={32} />
                    </div>
                    <div style={{ gridColumn: "span 12", textAlign: "center" }}>
                      <MUITextField
                        isDisabled
                        value=""
                        errors={{
                          consultedUsers: {
                            message: "Error: cannot retrieve consulted users.",
                          },
                        }}
                      />
                    </div>
                  </div>
                )}
                {!isLoadingForwarded && forwardArray?.length === 0 && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: grid12,
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ gridColumn: "span 12", textAlign: "center" }}>
                      <Icon iconName="icon_abb_dias icon-abb_warning" size={32} />
                    </div>
                    <div style={{ gridColumn: "span 12", textAlign: "center" }}>
                      <MUITextField
                        isDisabled
                        value=""
                        errors={{
                          consultedUsers: {
                            message: `Cannot forward the request${
                              confirmDialog.propertyName && confirmDialog.propertyValue
                                ? ` with property "${confirmDialog.propertyName}" and value "${confirmDialog.propertyValue}"`
                                : ""
                            } because there are no consulted users that can approve it.`,
                          },
                        }}
                      />
                    </div>
                  </div>
                )}
                {!isLoadingForwarded && forwardArray && forwardArray.length !== 0 && (
                  <>
                    The request will be forwarded to:
                    <ul style={ulStyle}>
                      {forwardArray.map((name: string) => (
                        <li key={name}>{name}</li>
                      ))}
                    </ul>
                    <br />
                  </>
                )}
              </>
            )}
            {!(
              confirmDialog.roleRequestAction === RoleRequestApprovalActionEnum.Forward &&
              !(!isLoadingForwarded && forwardArray && forwardArray.length !== 0)
            ) && (
              <>
                Add a message to the{" "}
                {confirmDialog.roleRequestAction &&
                  fromActionVerbToActionName(confirmDialog.roleRequestAction)}{" "}
                (optional):
                <MUITextField
                  label={forwardMessage.label}
                  multiline
                  rows={4}
                  value={forwardMessage.message}
                  maxCharacters={FORWARD_MESSAGE_MAX_CHARS}
                  addCharacterCountAsDescription
                  onChange={handleForwardMessageChange}
                  onBlur={formatMessageReset}
                />
              </>
            )}
          </>
        }
        secondaryButtonText="Cancel"
        secondaryButtonConfig={{
          isDisabled: isApproving,
        }}
        primaryButtonText={
          !(
            forwardListError &&
            confirmDialog.roleRequestAction === RoleRequestApprovalActionEnum.Forward
          ) && !isLoadingForwarded
            ? "Confirm"
            : undefined
        }
        onClickPrimaryButton={onClick}
        isPrimaryButtonDisabled={isApproving}
        primaryButtonConfig={{
          isLoading: isApproving,
          type: "primary",
          size: 32,
          id: "confirm-button",
        }}
      />

      <MUIDialog
        id="comment-dialog"
        variant="information"
        open={commentDialog.open}
        onClose={setCommentDialogOpenFalse}
        heading={`Message from the ${fromStatusVerbToApproverName(
          commentDialog.rraStatus
        )}:`}
        content={
          <span style={{ overflowWrap: "break-word" }}>{commentDialog.rraComment}</span>
        }
        closeIconConfig={{
          isDisabled: false,
          tooltipText: "Close",
        }}
      />
    </div>
  )
}
